import { useCallback, useState } from "react";
import { useRoomContext, useTrackToggle } from "@livekit/components-react";
import { Track } from "livekit-client";
import { ConnectionState } from "livekit-client";
import { PARTICIPANT_ROLES } from "@/react/helpers/liveStreamRoomHelpers";
import {
  selectCameraEnabled,
  useLiveSettingsActions,
  useLiveStore,
} from "../../store";
import { useLivekitLocalMetadata } from "./useLivekitLocalMetadata";

export const useLivekitCamera = () => {
  const [cameraError, setCameraError] = useState<string | null>(null);
  const room = useRoomContext();
  const { metadata: localMetadata } = useLivekitLocalMetadata();
  const isConnectedAsHost =
    room.state === ConnectionState.Connected &&
    localMetadata.role === PARTICIPANT_ROLES.HOST;
  const isPreviewCameraEnabled = useLiveStore(selectCameraEnabled);
  const { setCameraEnabled } = useLiveSettingsActions();

  const {
    toggle,
    enabled: isTrackEnabled,
    pending: isCameraPending,
  } = useTrackToggle<Track.Source.Camera>({
    source: Track.Source.Camera,
    initialState: isConnectedAsHost ? isPreviewCameraEnabled : undefined,
  });

  const isCameraEnabled = isConnectedAsHost
    ? isTrackEnabled
    : isPreviewCameraEnabled;

  const toggleCamera = useCallback(async () => {
    try {
      if (isConnectedAsHost) {
        await toggle();
      }
      setCameraEnabled(!isCameraEnabled);
      setCameraError(null);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "";
      setCameraError(errorMessage);
      console.error("Could not toggle camera:", errorMessage);
    }
  }, [
    isConnectedAsHost,
    isCameraEnabled,
    toggle,
    setCameraEnabled,
    setCameraError,
  ]);

  return {
    isCameraEnabled,
    isCameraPending,
    cameraError,
    toggleCamera,
  };
};
